/**
 * REST user-profile API client (D-2: REST for user CRUD; AppSync retired).
 *
 * Talks to the user-server via API Gateway (JWT-authed), same pattern as the
 * trips client: `Authorization: <accessToken>` (no Bearer — API Gateway's
 * Cognito authorizer). Replaces the AppSync GraphQL user-data path.
 *
 *   GET  {base}/user/{userId}   → UserProfile | 404
 *   PUT  {base}/user/{userId}   → UserProfile   (body: { profile })
 */

import { UserProfileInput } from '../auth/types/user';

const USER_API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_USER_ENDPOINT;

/** Server profile shape (mirrors the fields the AppSync getUser query returned). */
export interface UserProfile {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  nickname?: string | null;
  pictureUrl?: string | null;
  birthDate?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  locale?: string | null;
  zone?: string | null;
  lastSeenAt?: string | null;
  createdAt?: string | null;
}

function requireEndpoint(): string {
  if (!USER_API_ENDPOINT) {
    throw new Error('User API endpoint not configured (REACT_APP_APIGATEWAY_USER_ENDPOINT)');
  }
  return USER_API_ENDPOINT;
}

/** Fetch the caller's own profile. Returns null when the profile doesn't exist yet (404). */
export async function getUserProfile(userId: string, accessToken: string): Promise<UserProfile | null> {
  const url = `${requireEndpoint()}/user/${encodeURIComponent(userId)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: accessToken },
  });

  if (response.status === 404) return null;
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Failed to load user profile (${response.status}) ${detail}`.trim());
  }
  return (await response.json()) as UserProfile;
}

/** Update the caller's own profile. Server accepts { profile } (same shape as the old GraphQL UserInput). */
export async function updateUserProfile(
  userId: string,
  accessToken: string,
  profile: UserProfileInput
): Promise<UserProfile | null> {
  const url = `${requireEndpoint()}/user/${encodeURIComponent(userId)}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: accessToken },
    body: JSON.stringify({ profile }),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Failed to update user profile (${response.status}) ${detail}`.trim());
  }
  return (await response.json()) as UserProfile;
}
